import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { emptyArray } from 'ui-components/default-props/empty';

import { useGetSubscriptionCurrencyCode } from './use-get-subscription-currency-code';
import { useGetUserSeatsAddOnData } from './use-get-user-seats-addon-data';
import { EUR, GBP } from '../../../../constants/currency-codes';
import { Features, ShipmentVolume } from '../../../../constants/features';
import { BillingPlanIds, isXMigrationPlanCode } from '../../../../enums/billing-plan-ids';
import { useHasSingleOrderSourceForFreePlan } from '../../../../feature-gates/use-external-stores-access-feature-gate';
import { useIsLosingNonWalletAccess } from '../../../../hooks/non-wallet-access-hooks';
import { useActiveUsers } from '../../../../hooks/use-active-users';
import { useIsSellerFeatureEnabled } from '../../../../hooks/use-is-seller-feature-enabled';
import Global from '../../../../store/global-selectors';
import { DEFAULT_USER_ADDON_PRICE, getDefaultAnalyticsLicenseAddonPrice } from '../components/manage-subscription-drawer/constants';
import * as constants from '../constants';
import { DEFAULT_SHIPMENT_LIMIT } from '../constants/shipment-limits';
import { useManageSubscriptionDrawerContext } from '../context/manage-subscription-drawer-context';
import { messages } from '../messages';
import { findPlanReference, findPlanReferenceByCode, isFeatureIncludedInBillingPlan } from '../utils/plan-utilities';

/**
 * @typedef {import('../../../../prop-types/subscription-data-types.d.ts').MonetaryValue} MonetaryValue
 */

/**
 * @typedef {import('../../../../prop-types/subscription-data-types.d.ts').LimitTier} LimitTier
 */

const defaultMonetaryValue = { value: 0 };
export const UNLIMITED_PLAN_LIMIT = -1;

/*
 Note: Treat this file as a "repository" layer on top of our fragmented and duplicated redux state.
 Not sure what slice of redux is the correct one for a given concept?
 Likely they are all correct to some degree and different components chose different slices to use.
 Use the hooks here as a consoldated view and keep your hair.
*/

export const useSelectedPlan = () => {
  const { selectedPlanId } = useManageSubscriptionDrawerContext();
  const subscriptionData = useSelector(Global.selectSellerSubscriptionData);
  const { allPlans = [] } = subscriptionData ?? {};
  const cheapestPlan = allPlans.reduce(
    (prev, current) => (prev?.pricePerPeriod?.value < current?.pricePerPeriod?.value ? prev : current),
    undefined,
  );

  return useMemo(() => {
    return findPlanReference(selectedPlanId, subscriptionData) ?? cheapestPlan;
  }, [cheapestPlan, selectedPlanId, subscriptionData]);
};

// this method builds on useSelectedPlan to pick the BASE plan for the BYOA shadow plan implementation
export const useSelectedBasePlan = () => {
  const subscriptionData = useSelector(Global.selectSellerSubscriptionData);
  const initialSelection = useSelectedPlan();
  const selectedPlan = useMemo(() => {
    const { billingPlanId } = initialSelection;
    const byoaPlanEntries = Object.entries(subscriptionData?.planToNonWalletAccessPlanMap || {});
    const byoaPlanEntry = byoaPlanEntries.find(([, byoaPlan]) => byoaPlan.billingPlanId === billingPlanId);
    if (!byoaPlanEntry) {
      return initialSelection;
    }
    // Look in both allPlans and defaultProfilePlans for migration support
    const basePlanId = byoaPlanEntry[0];
    const basePlan =
      subscriptionData.allPlans.find((plan) => plan.billingPlanId === basePlanId) ??
      subscriptionData.defaultProfilePlans?.find((plan) => plan.billingPlanId === basePlanId);
    return basePlan ?? initialSelection;
  }, [initialSelection, subscriptionData]);

  return selectedPlan;
};

export const getPlanShipmentLimitMessage = (plan) => {
  const {
    billingPlanId,
    pricePerPeriod,
    shipmentLimit: { isUnlimited, limit, limitCode },
  } = plan;
  if (BillingPlanIds.SSEnterpriseAU === parseInt(billingPlanId, 10)) {
    return <FormattedMessage {...messages.tenThousandPlus} />;
  }
  if (isUnlimited && limitCode === constants.LIMIT_UNCHANGED) {
    return <FormattedMessage {...messages.unlimited} />;
  }
  if (limitCode === constants.LIMIT_HIGH_VOLUME_SHIPMENTS) {
    let costPerExtraShipment;
    if (pricePerPeriod?.code === GBP) {
      costPerExtraShipment = constants.COST_PER_SHIPMENT_OVER_10K_GBP;
    } else if (pricePerPeriod?.code === EUR) {
      costPerExtraShipment = constants.COST_PER_SHIPMENT_OVER_10K_EUR;
    } else {
      costPerExtraShipment = constants.COST_PER_SHIPMENT_OVER_10K_USD;
    }
    return <FormattedMessage {...messages.highVolumeShipmentLimit} values={{ costPerExtraShipment }} />;
  }
  if (limitCode === constants.LIMIT_HIGH_VOLUME_SHIPMENTS_15) {
    return (
      <FormattedMessage
        {...messages.highVolumeShipmentLimitVariable}
        values={{
          limit,
          costPerExtraShipment: constants.COST_PER_SHIPMENT_15_USD,
        }}
      />
    );
  }
  if (limitCode === constants.LIMIT_HIGH_VOLUME_SHIPMENTS_8) {
    return (
      <>
        <FormattedMessage {...messages.unlimited} />*
      </>
    );
  }
  if (limitCode === constants.LIMIT_10K_PLUS) {
    return <FormattedMessage {...messages.tenThousandPlus} />;
  }
  return limit;
};

export const useGetNumberOfActiveUsers = () => {
  const users = useActiveUsers();
  return users?.length ?? 0;
};

export const useGetNumberOfIncludedUserSeatsForPlan = (billingPlanId) => {
  const subscriptionData = useSelector(Global.selectSellerSubscriptionData);
  const planInQuestion = findPlanReference(billingPlanId, subscriptionData);

  if (!planInQuestion) {
    return 0;
  }

  const { userLimit } = planInQuestion;

  return userLimit?.limit ?? 0;
};

/**
 * Gets the trial-aware user limit for a given billing plan.
 * The backend provides the correct user limit accounting for trial state and plan context.
 * @returns {number} The user limit from the backend's trial-aware calculation
 */

export const useGetTrialAwareUserLimit = () => {
  const subscriptionPlanInfo = useSelector(Global.selectSellerSubscriptionPlanInfo);
  return subscriptionPlanInfo?.baseSupportedUserCount ?? 0;
};

export const useGetNumberOfIncludedAnalyticsLicensesForPlan = (billingPlanId) => {
  const subscriptionData = useSelector(Global.selectSellerSubscriptionData);
  const planInQuestion = findPlanReference(billingPlanId, subscriptionData);

  if (!planInQuestion) {
    return 0;
  }

  const totalLicenseLimit = planInQuestion?.analyticsLicenseLimit?.limit ?? 0;
  const additionalLicenses = planInQuestion?.additionalAnalyticsLicenses ?? 0;

  return Math.max(0, totalLicenseLimit - additionalLicenses);
};

export const useGetMinNumberOfUserSeats = (billingPlanId) => {
  const numberOfIncludedUserSeats = useGetNumberOfIncludedUserSeatsForPlan(billingPlanId);
  const numberOfActiveUsers = useGetNumberOfActiveUsers();

  const minNumberOfSeatsToCover = Math.max(0, numberOfActiveUsers - numberOfIncludedUserSeats);
  return minNumberOfSeatsToCover;
};

/**
 * Trial-aware version of useGetMinNumberOfUserSeats.
 * During trial, accounts for trial override limits when calculating required user seats.
 * @param {string} billingPlanId - The billing plan ID to calculate for
 * @returns {number} The minimum number of additional user seats needed
 */
export const useGetTrialAwareMinNumberOfUserSeats = (billingPlanId) => {
  const numberOfIncludedUserSeats = useGetTrialAwareUserLimit(billingPlanId);
  const numberOfActiveUsers = useGetNumberOfActiveUsers();

  // Math.Max because numberOfActiveUsers - numberOfIncludedUserSeats can be less than 0
  const minNumberOfSeatsToCover = Math.max(0, numberOfActiveUsers - numberOfIncludedUserSeats);
  return minNumberOfSeatsToCover;
};

export const usePricePerAdditionalUserForBillingPlanId = (billingPlanId) => {
  const { currentBillingPlan, currentSubscription, allPlans, defaultProfilePlans } = useSelector(Global.selectSellerSubscriptionData);

  return useMemo(() => {
    const isCurrentPlan = billingPlanId === currentBillingPlan?.billingPlanId;
    if (isCurrentPlan && currentSubscription?.pricePerAdditionalUser) {
      // Use current subscription data for current plan
      return currentSubscription.pricePerAdditionalUser;
    }

    // Include defaultProfilePlans for migration
    const plan =
      allPlans.find((p) => p.billingPlanId === billingPlanId) ?? defaultProfilePlans?.find((p) => p.billingPlanId === billingPlanId);
    if (plan?.userAddOnPrice) {
      return plan.userAddOnPrice;
    }

    return {
      value: DEFAULT_USER_ADDON_PRICE,
    };
  }, [allPlans, defaultProfilePlans, billingPlanId, currentBillingPlan?.billingPlanId, currentSubscription?.pricePerAdditionalUser]);
};

/**
 * @param {string} billingPlanId
 * @param {number} additionalUserCount
 * @returns {MonetaryValue}
 */
export const useAdditionalUserCountCostForBillingPlanId = (billingPlanId, additionalUserCount) => {
  const pricePerAdditionalUser = usePricePerAdditionalUserForBillingPlanId(billingPlanId);

  return useMemo(() => {
    return {
      value: additionalUserCount * pricePerAdditionalUser.value,
      code: pricePerAdditionalUser.code,
    };
  }, [additionalUserCount, pricePerAdditionalUser.code, pricePerAdditionalUser.value]);
};

/**
 * @param {string} billingPlanId
 * @returns {{ value: number, code?: string, pricingCode: string }}
 */
export const usePricePerAdditionalAnalyticsLicenseForBillingPlanId = (billingPlanId) => {
  const { allPlans, defaultProfilePlans, currentBillingPlan } = useSelector(Global.selectSellerSubscriptionData);

  return useMemo(() => {
    if (billingPlanId === currentBillingPlan?.billingPlanId && currentBillingPlan?.analyticsLicenseAddOnPrice) {
      return currentBillingPlan.analyticsLicenseAddOnPrice;
    }

    const plan =
      allPlans?.find((p) => p.billingPlanId === billingPlanId) ?? defaultProfilePlans?.find((p) => p.billingPlanId === billingPlanId);
    if (plan?.analyticsLicenseAddOnPrice) {
      return plan.analyticsLicenseAddOnPrice;
    }

    return {
      value: getDefaultAnalyticsLicenseAddonPrice(billingPlanId),
    };
  }, [allPlans, defaultProfilePlans, billingPlanId, currentBillingPlan?.billingPlanId, currentBillingPlan?.analyticsLicenseAddOnPrice]);
};

/**
 * @param {string} billingPlanId
 * @param {number} additionalLicenseCount
 * @returns {MonetaryValue}
 */
export const useAdditionalAnalyticsLicenseCountCostForBillingPlanId = (billingPlanId, additionalLicenseCount) => {
  const pricePerLicense = usePricePerAdditionalAnalyticsLicenseForBillingPlanId(billingPlanId);

  return useMemo(() => {
    return {
      value: additionalLicenseCount * pricePerLicense.value,
      code: pricePerLicense.code,
    };
  }, [additionalLicenseCount, pricePerLicense.code, pricePerLicense.value]);
};

export const useIsFeatureIncludedInAnyBillingPlan = (featureId) => {
  const subscriptionData = useSelector(Global.selectSellerSubscriptionData);
  const allPlans = subscriptionData.allPlans ?? [];
  return allPlans.some((plan) => isFeatureIncludedInBillingPlan(plan, featureId));
};

/**
 * @param {string} featureId
 * @returns {BillingPlan}
 */
export const useFirstPlanWithIncludedFeature = (featureId) => {
  const subscriptionData = useSelector(Global.selectSellerSubscriptionData);
  const allPlans = subscriptionData.allPlans ?? emptyArray;

  return useMemo(() => {
    return allPlans.find((plan) => isFeatureIncludedInBillingPlan(plan, featureId));
  }, [allPlans, featureId]);
};

export const useGetDefaultZeroCost = () => {
  const code = useGetSubscriptionCurrencyCode();
  return useMemo(() => {
    return {
      value: 0,
      code,
    };
  }, [code]);
};

/*
 * Use this hook to get the plan price from a given plan object in redux instead of footgunning on what property to use
 *
 * [Note]: pricingCode - plans often have a pricingCode e.g. { priceCode : "PricingUnchanged" } property. This property is completely deprecated and unused.
 * [TODO]: Consolidate with ss-ui-client\src\pages\settings\payment-and-subscription\hooks\use-get-coupon-adjusted-monthly-plan-cost.js
 */
export const useGetPlanPrice = (plan) => {
  const billingCurrencyCode = useGetSubscriptionCurrencyCode();
  const { currentBillingPlan, currentSubscription } = useSelector(Global.selectSellerSubscriptionData);
  const isCurrentPlan = Boolean(plan?.billingPlanId) && plan.billingPlanId === currentBillingPlan?.billingPlanId;
  const pricePerPeriod = isCurrentPlan
    ? (currentSubscription?.pricePerPeriod?.value ?? plan?.pricePerPeriod?.value)
    : plan?.pricePerPeriod?.value;
  const price = pricePerPeriod || 0;

  return useMemo(
    () => ({
      code: billingCurrencyCode,
      value: price,
    }),
    [billingCurrencyCode, price],
  );
};

export const useGetCurrentSubscriptionPlan = () => {
  const subscriptionData = useSelector(Global.selectSellerSubscriptionData);
  const subscriptionPlanInfo = useSelector(Global.selectSellerSubscriptionPlanInfo);
  const planInfo = useSelector(Global.selectSellerPlanInfo);

  const planCode = subscriptionData.currentBillingPlan?.code || subscriptionPlanInfo?.planCode || planInfo.plan?.code;

  const billingPlan = findPlanReferenceByCode(planCode, subscriptionData);

  return billingPlan;
};

const getPendingShipmentLimit = (pendingBillingAddOns, pendingPlan) => {
  // Check if there's a shipmentvolume add-on in pending changes
  const shipmentVolumeAddOn = pendingBillingAddOns?.find((addon) => addon.code === ShipmentVolume.toLowerCase());

  if (shipmentVolumeAddOn) {
    return shipmentVolumeAddOn.quantity;
  }

  if (isXMigrationPlanCode(pendingPlan?.code)) {
    const codeSplit = pendingPlan.code.split('_');
    const pendingVolume = Number.parseInt(codeSplit[1], 10);
    return pendingVolume;
  }

  // Fall back to pending plan's default shipment limit
  return pendingPlan?.shipmentLimit?.limit ?? 0;
};

export const useGetPendingSubscriptionChangeData = () => {
  const subscriptionData = useSelector(Global.selectSellerSubscriptionData);
  const subscriptionPlanInfo = useSelector(Global.selectSellerSubscriptionPlanInfo);
  const planInfo = useSelector(Global.selectSellerPlanInfo);
  const currentPlan = useGetCurrentSubscriptionPlan();

  const pendingPlanCode =
    subscriptionData.currentSubscription?.pendingPlanCode || subscriptionPlanInfo.pendingSubscription?.pendingPlanCode;

  const pendingPlanName =
    subscriptionData.currentSubscription?.pendingPlanName ||
    subscriptionPlanInfo.pendingSubscription?.planName ||
    planInfo?.pendingSubscription;

  const pendingDateTimeUTC =
    subscriptionData.currentSubscription?.pendingStartDateUTC ||
    subscriptionPlanInfo.pendingSubscription?.pendingStartDateTimeUTC ||
    planInfo?.pendingDateTimeUTC;

  const isLosingByoaAccess = useIsLosingNonWalletAccess();
  const isLosingByoaAccessOnly = (() => {
    if (!isLosingByoaAccess || !pendingPlanCode) return false;
    const pendingPlanRef = findPlanReferenceByCode(pendingPlanCode, subscriptionData);
    const { planToNonWalletAccessPlanMap = {} } = subscriptionData;
    const byoaVariantOfPending = planToNonWalletAccessPlanMap[pendingPlanRef?.billingPlanId];
    return byoaVariantOfPending?.billingPlanId === currentPlan?.billingPlanId;
  })();

  if (!pendingPlanName || !pendingDateTimeUTC || !pendingPlanCode) {
    return null;
  }

  // note that this is finding the wrong plan, it's pulling Standard name for starter code
  const pendingPlan = { ...findPlanReferenceByCode(pendingPlanCode, subscriptionData), code: pendingPlanCode };
  const pendingShipmentLimit = getPendingShipmentLimit(subscriptionData.currentSubscription?.pendingBillingAddOns, pendingPlan);

  return {
    plan: pendingPlan,
    pendingShipmentLimit,
    dateTimeUtc: pendingDateTimeUTC,
    isLosingByoaAccess,
    isLosingByoaAccessOnly,
  };
};

/**
 * Gets the current subscription shipment limit
 * @returns {number}
 */
export const useCurrentSubscriptionShipmentLimit = () => {
  return useSelector((state) => {
    const subscriptionData = Global.selectSellerSubscriptionData(state);
    return subscriptionData?.currentBillingPlan?.shipmentLimit?.limit || DEFAULT_SHIPMENT_LIMIT;
  });
};

/**
 * Gets the current subscription shipment limit tiers
 * @returns {LimitTier[]}
 */
export const useCurrentSubscriptionShipmentLimitTiers = () => {
  const { currentBillingPlan, currentSubscription } = useSelector(Global.selectSellerSubscriptionData);

  return currentSubscription?.shipmentVolumeTiers || currentBillingPlan?.shipmentLimit?.tiers;
};

/**
 * Gets the shipment limit tiers for a given billing plan
 * @param {string} billingPlanId
 * @returns {LimitTier[]}
 */
export const useShipmentLimitTiersForBillingPlan = (billingPlanId) => {
  const { currentBillingPlan, currentSubscription, allPlans, defaultProfilePlans } = useSelector(Global.selectSellerSubscriptionData);

  return useMemo(() => {
    const isCurrentPlan = billingPlanId === currentBillingPlan?.billingPlanId;

    if (isCurrentPlan && currentSubscription?.shipmentVolumeTiers) {
      // Use current subscription data for current plan
      return currentSubscription.shipmentVolumeTiers;
    }

    // Use all plans data for other plans, including defaultProfilePlans for migration
    const plan =
      allPlans.find((p) => p.billingPlanId === billingPlanId) ?? defaultProfilePlans?.find((p) => p.billingPlanId === billingPlanId);
    if (plan?.shipmentLimit?.tiers) {
      return plan.shipmentLimit.tiers;
    }

    return [
      {
        endingQuantity: plan?.shipmentLimit?.limit || DEFAULT_SHIPMENT_LIMIT,
        price: defaultMonetaryValue,
      },
    ];
  }, [allPlans, defaultProfilePlans, billingPlanId, currentBillingPlan?.billingPlanId, currentSubscription?.shipmentVolumeTiers]);
};

/**
 * Gets the shipment limit cost for a billing plan and shipment count
 * @param {string} billingPlanId - The billing plan to get the shipment limit cost for
 * @param {number} shipmentLimit - The shipment limit to get the cost of
 * @returns {MonetaryValue|undefined} The shipment cost
 */
export const useShipmentLimitCostForBillingPlan = (billingPlanId, shipmentLimit) => {
  const shipmentLimitTiers = useShipmentLimitTiersForBillingPlan(billingPlanId);

  return shipmentLimitTiers?.find((tier) => tier.endingQuantity === shipmentLimit)?.price;
};

/**
 * Gets the number of order sources allowed for the current billing plan
 * @returns {number} The number of order sources allowed, or -1 for unlimited
 */
export const useOrderSourceLimitForCurrentBillingPlan = () => {
  const isExternalStoreAccessEnabled = useIsSellerFeatureEnabled(Features.ExternalStoresAccess);
  const isCustomStoreEnabled = useIsSellerFeatureEnabled(Features.OrderImportCustomStore);
  const hasSingleOrderSourceLimit = useHasSingleOrderSourceForFreePlan();

  if (hasSingleOrderSourceLimit) {
    return 1;
  }

  if (isExternalStoreAccessEnabled || isCustomStoreEnabled) {
    return UNLIMITED_PLAN_LIMIT;
  }

  return 0;
};

const UNLIMITED_USERS = -1;
/**
 * Shared hook for consistent user limit data across all components
 * Uses the same logic as User Management page
 * @returns {{ baseSupportedUserCount: number, additionalUserCount: number, totalUserCount: number, hasUnlimitedUsers: boolean }} User limit data with consistent calculations
 */
export const useUserLimitData = () => {
  const subscriptionPlanInfo = useSelector(Global.selectSellerSubscriptionPlanInfo);
  const userAddOnData = useGetUserSeatsAddOnData();

  const baseSupportedUserCount = subscriptionPlanInfo?.baseSupportedUserCount ?? 0;
  const additionalUserCount = userAddOnData?.quantity ?? 0;
  const totalUserCount = baseSupportedUserCount + additionalUserCount;
  const hasUnlimitedUsers = baseSupportedUserCount === UNLIMITED_USERS;

  return {
    baseSupportedUserCount,
    additionalUserCount,
    totalUserCount,
    hasUnlimitedUsers,
  };
};
